import styled, { css, keyframes } from 'styled-components';
import { LARGE_SCREEN_WIDTH_REM } from '../../utils/constants';

const textDirection = theme => (theme.dir === 'rtl' ? 'right' : 'left');
const rtlSwitch = theme => (theme.dir === 'rtl' ? 'left' : 'right');
const fold_up_left = keyframes`
  0% {
    transform: scale(1,1);
  }
  50% {
    transform: scaleY(0.1);
  }
  100% {
    transform: scale(0, 0);
  }
`;

const fade_in = keyframes`
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
`;


export const CardContainer = styled.div`
  background-color: rgb(255, 255, 255);
  border-radius: 0.75rem;
  border-width: 0;
  box-sizing: inherit;
  box-shadow: 0 0.25rem 1rem rgba(0, 0, 0, 0.1);
  display: inline-block;
  position: relative;
  height: 5.75rem;
  white-space: normal;

  &:focus {
    outline: none !important;
  }

  @media screen and (min-width: ${LARGE_SCREEN_WIDTH_REM}) {
    height: 6.375rem;
    box-shadow: 0px 12px 40px rgba(106, 106, 106, 0.1);
  }
  ${({ theme: { dir, fullScreen } }) =>
    fullScreen
      ? `
      margin: 0 1.25rem 0.75rem 1.25rem;
      width: 90%;
      @media screen and (min-width: 48rem) {
        max-width: 21.437rem;
      }
      @media screen and (min-width: ${LARGE_SCREEN_WIDTH_REM}) {
        max-width: 25rem;
        margin: 0 1rem 2rem 1rem;
        height: 9.2rem;
      }
    `
      : `
      width: 17.75rem;
      ${dir !== 'rtl' ? `margin-right: 1rem;` : `margin-left: 1rem;`}
      @media screen and (min-width: ${LARGE_SCREEN_WIDTH_REM}) {
        width: 17.5rem;
        &: not(:last-of-type) {
          ${dir !== 'rtl' ? `margin-right: 1.5rem;` : `margin-left: 1.5rem;`}
        }
        &:last-of-type {
          ${dir !== 'rtl' ? `margin-right: 0;` : `margin-left: 0;`}
        }
      }
    `}
  ${({ transitionName }) => css`
    &.${transitionName}-enter {
      opacity: 0;
    }
    &.${transitionName}-enter-active {
      animation: ${fade_in} 500ms ease-in;
    }
    &.${transitionName}-exit {
      opacity: 1;
    }
    &.${transitionName}-exit-active {
      opacity: 0;
      transition: all 300ms ease-in;
    }
  `}
`;

export const Layout = styled.div`
  display: flex;
  align-content: flex-start;
  justify-content: flex-start;
  height: 100%;
  width: 100%;
`;

export const tileImage = css`
  height: 100%;
  flex-shrink: 0;
  flex-grow: 0;
  flex-basis: 4.75rem;
  pointer-events: none;
  background-size: cover;
  @media screen and (min-width: ${LARGE_SCREEN_WIDTH_REM}) {
    flex-basis: ${({ theme: { fullScreen } }) => (fullScreen ? `6.5rem;` : `4.375rem;`)};
  }
`;

export const LeadingStyle = css`
  background-image: ${({ image }) => image};
  background-position: center;
  background-repeat: no-repeat;
  border-radius: ${({ theme: { dir } }) =>
    dir === 'rtl' ? `0 0.75rem 0.75rem 0` : `0.75rem 0 0 0.75rem`};
  ${tileImage}
`;

export const Leading = styled.div`
  ${LeadingStyle}
`;

export const LottieContainer = styled.div`
  ${tileImage}
  > div {
    border-radius: ${({ theme: { dir } }) =>
      dir === 'rtl' ? `0 0.75rem 0.75rem 0` : `0.75rem 0 0 0.75rem`};
  }
`;

export const Trailing = styled.div`
  padding: 0.778rem 0.75rem 0.75rem 0.875rem;
  width: 13rem;
  box-sizing: border-box;
  flex-grow: 1;
  @media screen and (min-width: ${LARGE_SCREEN_WIDTH_REM}) {
    padding: 0.75rem 0.75rem 1.063rem 1rem;
    width: 13.125rem;
  }

  & > div {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
    height: 100%;
  }
`;

export const TitleStyle = css`
  color: rgba(64, 64, 64, 1);
  font-size: 0.75rem;
  line-height: 1.1;
  width: 90%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  @media screen and (min-width: ${LARGE_SCREEN_WIDTH_REM}) {
    font-size: ${({ theme: { fullScreen } }) => (fullScreen ? '1rem' : '0.75rem')};
  }
`;

export const Title = styled.div`
  ${TitleStyle}
`;

export const TitleIcon = styled.img`
  float: left;
`;

export const SubtitleStyle = css`
  color: rgba(64, 64, 64, 1);
  font-size: 0.688rem;
  line-height: 0.813rem;
  font-weight: 300;
  -webkit-line-clamp: ${({ computedFontSize }) =>
    computedFontSize > 16 ? 1 : 2}; /* stylelint-disable-line */
  display: -webkit-box; /* stylelint-disable-line */
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical; /* stylelint-disable-line */
  width: 98%;
  vertical-align: baseline;
  position: relative;
  top: -0.4em;
  
  @media screen and (min-width: ${LARGE_SCREEN_WIDTH_REM}) {
    font-size: 0.75rem;
    line-height: 1rem;
    max-height: calc(1rem * 2);
  }
`;

export const Subtitle = styled.div`
  ${SubtitleStyle}
`;

export const LinkStyle = css`
  appearance: none;
  border: none;
  background-color: transparent;
  padding: 0;
  font-size: 0.688rem;
  line-height: 1.1;
  text-decoration: none;
  color: rgba(2, 122, 174, 1);
  cursor: pointer;
  width: 95%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: ${/* sc-custom "left" */ props => textDirection(props.theme)};
  &:focus{
    outline: none;
  }
  &:focus-visible{
    outline: 1px solid #d3d3d3;
  }
  &:active,
  &:hover {
    text-decoration: none;
  }
  @media screen and (min-width: ${LARGE_SCREEN_WIDTH_REM}) {
    font-size: ${({ theme: { fullScreen } }) => (fullScreen ? '1rem' : '0.625rem')};
    line-height: ${({ theme: { fullScreen } }) => (fullScreen ? '1.25rem' : '0.75rem')};
  }
`;

export const Link = styled.a`
  ${LinkStyle}
`;

export const CloseTarget = styled.button`
  width: 2rem;
  height: 2rem;
  position: absolute;
  text-align: right;
  cursor: pointer;
  border: 0;
  background: transparent;
  top: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  > svg {
    width: 0.75rem;
    height: 0.75rem;
    cursor: pointer;
  }
  &:focus{
    outline:focus;
  }
  &:focus-visible{
    outline: 1px solid #d3d3d3 !important;
  }
  
  ${/* sc-custom "left" */ props => rtlSwitch(props.theme)}: 0;
`;
